/**
 * AI Insights Panel - Centralized Recommendations
 * ================================================
 * Shows consistent AI insights across all pages
 * Shared component for Radar, Clustering, Forecaster, etc.
 * 
 * PHASE 3 UPDATE: Now supports Brain-powered unified insights.
 * When USE_BRAIN_FOR_INSIGHTS=true, fetches from /api/brain/findings/unified
 * instead of legacy /api/insights endpoints.
 * 
 * PROPS:
 * - maxInsights: CRITICAL - Controls API fetch limit (default: 10)
 * - category: Filter by category (limits to 5)
 * - compact: Show condensed version (limits to 3)
 * - filterByRetailer: TRUE for retailer pages, FALSE for overview
 * - useBrain: Override to force Brain source (default: auto from env)
 * 
 * IMPORTANT: maxInsights affects BOTH API fetch AND display
 * - Overview dashboard should use maxInsights={20-30}
 * - Brand-level insights are LOW priority (sort last)
 * - If brand insights missing, check maxInsights >= total count
 * 
 * HONEST LABELING:
 * - Brain-Powered insights: From unified Brain pipeline (purple badge)
 * - ML-Powered insights: From real trained models (Forecasting, Clustering, Stockout predictions)
 * - Data-Driven insights: From statistical thresholds and pattern detection
 * - Brand-Level insights: Cross-retailer analysis (purple badge)
 */

import { useState, useEffect, useCallback } from 'react';
import { Sparkles, TrendingUp, AlertTriangle, Target, DollarSign, Brain, BarChart3, CheckCircle, Star, Zap } from 'lucide-react';
import { apiClient } from '../lib/api-client';

interface Insight {
  id: string;
  insight_type: string;
  category: string;
  priority: string;
  title: string;
  description: string;
  recommendation: string;
  dollar_impact: number;
  confidence_score: number;
  source: string;
  retailer_id?: string;
  is_acknowledged?: boolean;
  acknowledged_at?: string;
  metadata?: {
    model_name?: string;
    model_accuracy?: string;
  };
}

import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import { useOverviewDate } from '../contexts/OverviewDateContext';
import { useTenant } from '../contexts/TenantContext';
import { getRetailerInfo } from '../config/retailers';
import CompactInsightCard from './CompactInsightCard';
import InsightDetailDialog from './InsightDetailDialog';

interface AIInsightsPanelProps {
  category?: string;
  maxInsights?: number;
  compact?: boolean;
  filterByRetailer?: boolean; // Set to true to filter insights by selected retailer (for single-retailer views)
  // useBrain prop removed - Brain is now the ONLY source
}

// BRAIN IS NOW THE SINGLE SOURCE OF TRUTH
// Legacy insight generation has been removed - all insights come from Brain
const USE_BRAIN_FOR_INSIGHTS = true; // Always true - Brain is the only source

export default function AIInsightsPanel({ category, maxInsights, compact = false, filterByRetailer = false }: AIInsightsPanelProps) {
  // Brain is ALWAYS the source now - no legacy path
  const shouldUseBrain = true;
  // Support both contexts: FiscalWeek (retailer dashboard) and OverviewDate (overview dashboard)
  const fiscalWeekContext = useFiscalWeekContext();
  const { selectedRetailerId } = useTenant();
  
  // Safely try to get overview date context (only available on overview dashboard)
  let overviewDateContext;
  try {
    overviewDateContext = useOverviewDate();
  } catch (error) {
    // OverviewDateProvider not available - that's ok, we're on a retailer dashboard
    overviewDateContext = null;
  }
  
  // Determine which context is available
  const selectedFiscalWeek = fiscalWeekContext?.selectedFiscalWeek;
  const selectedDateRange = overviewDateContext?.selectedDateRange;
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedInsightForDetail, setSelectedInsightForDetail] = useState<Insight | null>(null);

  const fetchInsights = useCallback(async () => {
    try {
      // PHASE 3: Use Brain-powered unified API when enabled
      if (shouldUseBrain) {
        console.log('[AIInsightsPanel] Using Brain-powered unified API');
        const params = new URLSearchParams();
        params.append('limit', (maxInsights || 10).toString());
        
        // Map category to source filter
        if (category) {
          const sourceMap: Record<string, string> = {
            'inventory': 'retail',
            'revenue': 'retail',
            'customer': 'dtc',
            'marketing': 'google_ads',
            'pattern': 'pattern',
          };
          const source = sourceMap[category] || 'all';
          params.append('source', source);
        }
        
        // Filter by retailer if requested
        if (filterByRetailer && selectedRetailerId) {
          params.append('retailerId', selectedRetailerId);
        }
        
        const queryString = params.toString();
        const response = await apiClient.get<{ 
          ok: boolean; 
          insights: Array<{
            id: string;
            type: string;
            category: string;
            priority: string;
            title: string;
            description: string;
            recommendation?: string;
            dollarImpact: number | null;
            confidenceScore: number | null;
            source: string;
            retailerId?: string;
            isBrainPowered: boolean;
            traceId?: string;
          }>;
        }>(`/brain/findings/unified?${queryString}`);
        
        if (response.ok && response.insights && response.insights.length > 0) {
          // Convert Brain format to legacy Insight format for UI compatibility
          const convertedInsights: Insight[] = response.insights.map(bi => ({
            id: bi.id,
            insight_type: bi.type,
            category: bi.category,
            priority: bi.priority,
            title: bi.title,
            description: bi.description,
            recommendation: bi.recommendation || '',
            dollar_impact: bi.dollarImpact || 0,
            confidence_score: bi.confidenceScore || 80,
            source: bi.isBrainPowered ? 'brain_powered' : bi.source,
            retailer_id: bi.retailerId,
            is_acknowledged: false,
            metadata: {
              isBrainPowered: bi.isBrainPowered,
              traceId: bi.traceId,
            },
          }));
          
          setInsights(convertedInsights);
          console.log(`[AIInsightsPanel] Brain: Loaded ${convertedInsights.length} insights`);
          setIsLoading(false);
          return;
        }
        
        // Brain returned no insights - try legacy endpoint as fallback
        console.log('[AIInsightsPanel] Brain returned 0 insights, trying legacy fallback');
      }
      
      // FALLBACK: Try legacy insights API if Brain returns nothing
      const legacyParams = new URLSearchParams();
      legacyParams.append('limit', (maxInsights || 10).toString());
      
      // Use overview endpoint for calendar dates, regular endpoint for fiscal weeks
      let apiEndpoint = '/insights';
      if (selectedDateRange) {
        legacyParams.append('start_date', selectedDateRange.start);
        legacyParams.append('end_date', selectedDateRange.end);
        apiEndpoint = '/insights/overview';
      } else if (selectedFiscalWeek) {
        legacyParams.append('fiscalWeek', selectedFiscalWeek.toString());
        if (filterByRetailer && selectedRetailerId) {
          legacyParams.append('retailer_id', selectedRetailerId);
        }
      }
      
      try {
        const legacyResponse = await apiClient.get<{ success: boolean; insights: Insight[] }>(
          `${apiEndpoint}?${legacyParams.toString()}`
        );
        
        if (legacyResponse.success && legacyResponse.insights?.length > 0) {
          setInsights(legacyResponse.insights);
          console.log(`[AIInsightsPanel] Legacy fallback: Loaded ${legacyResponse.insights.length} insights`);
        } else {
          setInsights([]);
          console.log('[AIInsightsPanel] No insights from Brain or legacy');
        }
      } catch (legacyError) {
        console.error('[AIInsightsPanel] Legacy fallback failed:', legacyError);
        setInsights([]);
      }
    } catch (error) {
      console.error('[AIInsightsPanel] Error fetching insights:', error);
      setInsights([]);
    } finally {
      setIsLoading(false);
    }
  }, [category, selectedFiscalWeek, selectedDateRange, filterByRetailer, selectedRetailerId, maxInsights, shouldUseBrain]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // BRAIN IS THE SINGLE SOURCE - No separate "generate" action needed
  // Users run "Run Brain" from Command Center, which generates all insights
  // This panel just displays what the Brain has found

  const handleAcknowledge = useCallback(async (insightId: string) => {
    // Update local state immediately
    setInsights(prevInsights => 
      prevInsights.map(insight => 
        insight.id === insightId 
          ? { ...insight, is_acknowledged: true, acknowledged_at: new Date().toISOString() }
          : insight
      )
    );
    console.log('[AIInsightsPanel] Acknowledged insight:', insightId);
  }, []);

  const getPriorityColor = (priority: string, insightType?: string) => {
    // Achievements get green celebratory treatment
    if (insightType === 'achievement') {
      return 'border-green-300 bg-green-50';
    }
    
    switch (priority) {
      case 'critical': return 'border-red-200 bg-red-50';
      case 'high': return 'border-orange-200 bg-orange-50';
      case 'medium': return 'border-yellow-200 bg-yellow-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  const getPriorityBadge = (priority: string, insightType?: string) => {
    // Achievements show a celebratory badge
    if (insightType === 'achievement') {
      return (
        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-800 flex items-center gap-1">
          <Star className="w-3 h-3" />
          WIN
        </span>
      );
    }
    
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800',
    };
    return <span className={`px-2 py-0.5 text-xs font-semibold rounded ${colors[priority]}`}>{priority.toUpperCase()}</span>;
  };

  const getSourceBadge = (source: string, metadata?: any) => {
    // Brain-powered insights get special treatment
    if (source === 'brain_powered' || metadata?.isBrainPowered) {
      return (
        <div className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 border border-purple-300">
          <Zap className="w-3 h-3" />
          <span>Brain-Powered</span>
        </div>
      );
    }
    
    const isMLPowered = source && (
      source.includes('_ml') || 
      source === 'stockout_risk_ml' || 
      source === 'forecast_anomaly_ml' || 
      source === 'cluster_opportunity_ml'
    );

    if (isMLPowered && metadata?.model_name) {
      return (
        <div className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800 border border-purple-300">
          <Brain className="w-3 h-3" />
          <span>{metadata.model_name}</span>
        </div>
      );
    } else if (isMLPowered) {
      return (
        <div className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800 border border-purple-300">
          <Brain className="w-3 h-3" />
          <span>Analytics-Powered</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800 border border-blue-300">
          <BarChart3 className="w-3 h-3" />
          <span>Data-Driven</span>
        </div>
      );
    }
  };

  const getBrandLevelBadge = (category: string) => {
    const isBrandLevel = category === 'distribution_optimization' || 
                         category === 'portfolio_risk' ||
                         category === 'pricing_optimization';
    
    if (!isBrandLevel) return null;
    
    return (
      <div className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 border border-purple-300">
        <Target className="w-3 h-3" />
        <span>Brand-Level</span>
      </div>
    );
  };

  const getRetailerBadge = (retailerId?: string) => {
    if (!retailerId) return null;
    
    const retailerInfo = getRetailerInfo(retailerId);
    if (!retailerInfo) return null;

    return (
      <div 
        className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border-2"
        style={{ 
          backgroundColor: retailerInfo.colors.light,
          borderColor: retailerInfo.colors.medium,
          color: retailerInfo.colors.dark || retailerInfo.colors.primary
        }}
      >
        <span>{retailerInfo.name}</span>
      </div>
    );
  };

  if (isLoading) return null;

  // Compact mode for inline display (dashboard hero)
  if (compact) {
    const displayInsights = insights.slice(0, maxInsights || 3);
    
    if (displayInsights.length === 0) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-5 h-5 text-purple-600" />
            <h3 className="text-base font-bold text-gray-900">Brain Insights</h3>
          </div>
          <div className="text-center py-8">
            <Brain className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Run Brain from Command Center</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            <h3 className="text-base font-bold text-gray-900">Brain Insights</h3>
          </div>
          <button
            onClick={() => fetchInsights()}
            className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-semibold transition-all"
          >
            Refresh
          </button>
        </div>
        <div className="space-y-2">
          {displayInsights.map((insight) => (
            <CompactInsightCard
              key={insight.id}
              insight={insight}
              onClick={() => setExpandedId(insight.id)}
            />
          ))}
        </div>
        {insights.length > displayInsights.length && (
          <div className="mt-3 pt-3 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              +{insights.length - displayInsights.length} more insights below
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl border border-purple-300/50 shadow-lg mb-6">
      {/* AI Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-600 opacity-5"></div>
      
      {/* Header with Special AI Branding */}
      <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">PatternIQ Insights</h2>
              <p className="text-sm text-purple-100">Data-Driven Recommendations • Real-time Analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full flex items-center gap-2">
              <Brain className="w-4 h-4 text-white" />
              <span className="text-xs font-semibold text-white">Brain-Powered • {insights.length} Insights</span>
            </div>
            <button
              onClick={() => fetchInsights()}
              className="px-4 py-2 bg-white/90 hover:bg-white text-purple-700 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-6 bg-white">
        
        {insights.length === 0 ? (
          <div className="text-center py-12">
            <Brain className="w-12 h-12 text-purple-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Insights Available</h3>
            <p className="text-sm text-gray-600 mb-4">
              Run the Brain from Command Center to generate insights.
              <br />
              <span className="text-xs text-gray-500">
                {selectedFiscalWeek 
                  ? `Week ${selectedFiscalWeek}`
                  : selectedDateRange 
                    ? `${selectedDateRange.start} to ${selectedDateRange.end}`
                    : 'Select a time period'}
              </span>
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {insights.map((insight) => {
            const isExpanded = expandedId === insight.id;
            const retailerInfo = insight.retailer_id ? getRetailerInfo(insight.retailer_id) : null;
            
            return (
              <div 
                key={insight.id} 
                className={`border-2 rounded-xl transition-all cursor-pointer hover:shadow-xl hover:scale-[1.02] ${getPriorityColor(insight.priority, insight.insight_type)} ${isExpanded ? 'ring-2 ring-purple-400' : ''} ${insight.is_acknowledged ? 'opacity-50 bg-gray-100' : ''}`}
                onClick={() => setSelectedInsightForDetail(insight)}
              >
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getPriorityBadge(insight.priority, insight.insight_type)}
                    {getSourceBadge(insight.source, insight.metadata)}
                    {getBrandLevelBadge(insight.category)}
                    {getRetailerBadge(insight.retailer_id)}
                    {insight.is_acknowledged && (
                      <div className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-800 border border-green-300">
                        <CheckCircle className="w-3 h-3" />
                        Acknowledged
                      </div>
                    )}
                  </div>
                  {insight.dollar_impact && (
                    <div className="flex flex-col items-end">
                      <div className={`flex items-center gap-1 text-lg font-bold ${insight.insight_type === 'achievement' ? 'text-green-700' : 'text-gray-900'}`}>
                        <DollarSign className="w-4 h-4" />
                        {Math.round(insight.dollar_impact).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {insight.insight_type === 'achievement' ? 'Revenue Generated' : 'Weekly Impact'}
                      </div>
                    </div>
                  )}
                </div>

                <h4 className="font-semibold text-gray-900 mb-2">{insight.title}</h4>
                <p className="text-sm text-gray-700">{insight.description}</p>
                
                {!isExpanded && (
                  <div className="mt-2 text-xs text-purple-600 font-medium flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Click for analysis →
                  </div>
                )}
              </div>

              {isExpanded && insight.recommendation && (
                <div className="px-4 pb-4 border-t border-current/20 bg-white/50">
                  <div className="mt-3">
                    <div className="flex items-start gap-2 mb-3">
                      <Target className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900 mb-1">Recommended Actions:</div>
                        <div className="text-sm text-gray-700">{insight.recommendation}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs bg-white rounded p-3 border">
                      <div>
                        <span className="text-gray-500">Type:</span>
                        <span className="ml-2 font-medium">{insight.insight_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Confidence:</span>
                        <span className="ml-2 font-medium">{Math.round(insight.confidence_score)}%</span>
                      </div>
                      {insight.metadata?.model_accuracy && (
                        <div>
                          <span className="text-gray-500">Model Accuracy:</span>
                          <span className="ml-2 font-medium">{insight.metadata.model_accuracy}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Week:</span>
                        <span className="ml-2 font-medium">{insight.fiscal_week}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedInsightForDetail && (
        <InsightDetailDialog
          insight={selectedInsightForDetail}
          onClose={() => setSelectedInsightForDetail(null)}
          retailerName={selectedInsightForDetail.retailer_id ? getRetailerInfo(selectedInsightForDetail.retailer_id)?.name : undefined}
          onAcknowledge={handleAcknowledge}
        />
      )}
    </div>
  );
}
